import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Edit, Calendar, ChevronUp, ChevronDown, AlertTriangle, Search, X, Printer } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { printClientIntakeForm } from '../../utils/printRegistrationForm';

// Format date helper
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return '-';
  }
};

// Status badge component - ERP: Now uses client_status field
const StatusBadge = ({ client }) => {
  const status = client.client_status;
  
  if (status === 'onboarding') {
    return (
      <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-500/20 text-yellow-200">
        Onboarding
      </span>
    );
  } else if (status === 'inactive' || client.is_active === false) {
    return (
      <span className="px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-200">
        Inactive
      </span>
    );
  }
  // Default to Active (status === 'active' or legacy is_active === true)
  return (
    <span className="px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-200">
      Active
    </span>
  );
};

// Alerts indicator component
const AlertsIndicator = ({ client }) => {
  const hasInjury = client.injury_history_note && client.injury_history_note.trim().length > 0;
  
  if (!hasInjury) return <span className="text-blue-400">-</span>;
  
  return (
    <div className="flex items-center gap-1">
      <span 
        className="bg-red-500/20 text-red-200 px-2 py-1 rounded text-xs flex items-center gap-1 cursor-help"
        title={client.injury_history_note}
      >
        <AlertTriangle className="w-3 h-3" />
        Injury
      </span>
    </div>
  );
};

// Training frequency badge
const FrequencyBadge = ({ frequency }) => {
  let colorClass = 'bg-gray-500/20 text-gray-300';
  
  if (frequency >= 8) {
    colorClass = 'bg-green-500/20 text-green-200';
  } else if (frequency >= 4) {
    colorClass = 'bg-blue-500/20 text-blue-200';
  } else if (frequency >= 1) {
    colorClass = 'bg-yellow-500/20 text-yellow-200';
  } else {
    colorClass = 'bg-red-500/20 text-red-200';
  }
  
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${colorClass}`}>
      {frequency} sessions
    </span>
  );
};

// Sort icon component - moved outside to avoid re-creation on each render
const SortIcon = ({ columnKey, sortConfig }) => {
  if (sortConfig.key !== columnKey) {
    return <ChevronUp className="w-3 h-3 text-blue-400/30" />;
  }
  return sortConfig.direction === 'asc' 
    ? <ChevronUp className="w-3 h-3 text-blue-300" />
    : <ChevronDown className="w-3 h-3 text-blue-300" />;
};

const ClientsTable = ({ clients, t }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusToggles, setStatusToggles] = useState({ active: true, onboarding: true, inactive: false });
  const [sortConfig, setSortConfig] = useState({ key: 'full_name', direction: 'asc' });

  // Resolve a client's canonical status bucket
  const resolveStatus = (client) => {
    if (client.client_status === 'onboarding') return 'onboarding';
    if (client.client_status === 'inactive' || client.is_active === false) return 'inactive';
    return 'active';
  };

  const toggleStatus = (key) => {
    setStatusToggles(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Counts per status (from the full client list, pre-filter)
  const statusCounts = useMemo(() => {
    const counts = { active: 0, onboarding: 0, inactive: 0 };
    clients.forEach(c => { counts[resolveStatus(c)] += 1; });
    return counts;
  }, [clients]);

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filter and sort clients
  const filteredAndSortedClients = useMemo(() => {
    let result = [...clients];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(client => 
        client.full_name?.toLowerCase().includes(query) ||
        client.email?.toLowerCase().includes(query) ||
        client.phone?.includes(query)
      );
    }

    // Apply status toggle filter (multi-select). If all off, show none.
    result = result.filter(client => statusToggles[resolveStatus(client)]);

    // Apply sorting
    result.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // Handle numeric values
      if (sortConfig.key === 'completed_trainings' || sortConfig.key === 'training_frequency_30d') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      // Handle date values
      if (sortConfig.key === 'last_training_date') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [clients, searchQuery, statusToggles, sortConfig]);

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setStatusToggles({ active: true, onboarding: true, inactive: false });
  };

  const STATUS_CHIPS = [
    { key: 'active', label: 'Active', on: 'bg-green-500 text-white border-green-400', off: 'bg-green-900/20 text-green-300 border-green-400/20 hover:bg-green-800/30' },
    { key: 'onboarding', label: 'Onboarding', on: 'bg-yellow-500 text-black border-yellow-400', off: 'bg-yellow-900/20 text-yellow-300 border-yellow-400/20 hover:bg-yellow-800/30' },
    { key: 'inactive', label: 'Inactive', on: 'bg-red-500 text-white border-red-400', off: 'bg-red-900/20 text-red-300 border-red-400/20 hover:bg-red-800/30' },
  ];
  const filtersDirty = searchQuery || !statusToggles.active || !statusToggles.onboarding || statusToggles.inactive;

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full md:w-auto">
          {/* Search Input */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or phone..."
              className="input-field pl-10 pr-10 text-sm w-full"
              data-testid="client-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Status Quick-Filter Chips (multi-select) */}
          <div className="flex flex-wrap items-center gap-2" data-testid="client-status-chips">
            {STATUS_CHIPS.map(chip => (
              <button
                key={chip.key}
                onClick={() => toggleStatus(chip.key)}
                data-testid={`status-chip-${chip.key}`}
                aria-pressed={statusToggles[chip.key]}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                  statusToggles[chip.key] ? chip.on : chip.off
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${statusToggles[chip.key] ? 'bg-current opacity-90' : 'bg-current opacity-30'}`} />
                {chip.label}
                <span className="opacity-70">{statusCounts[chip.key]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Results count and clear */}
        <div className="flex items-center gap-3">
          <span className="text-blue-300 text-sm">
            {filteredAndSortedClients.length} of {clients.length} clients
          </span>
          {filtersDirty && (
            <button
              onClick={clearFilters}
              className="text-blue-400 hover:text-white text-sm flex items-center gap-1"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4" />
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="glass-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-blue-400/20 hover:bg-transparent">
              <TableHead 
                className="text-blue-200 cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('full_name')}
              >
                <div className="flex items-center gap-1">
                  Client Name
                  <SortIcon columnKey="full_name" sortConfig={sortConfig} />
                </div>
              </TableHead>
              <TableHead 
                className="text-blue-200 cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('is_active')}
              >
                <div className="flex items-center gap-1">
                  Status
                  <SortIcon columnKey="is_active" sortConfig={sortConfig} />
                </div>
              </TableHead>
              <TableHead 
                className="text-blue-200 cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('last_training_date')}
              >
                <div className="flex items-center gap-1">
                  Last Training
                  <SortIcon columnKey="last_training_date" sortConfig={sortConfig} />
                </div>
              </TableHead>
              <TableHead 
                className="text-blue-200 cursor-pointer hover:text-white transition-colors text-center"
                onClick={() => handleSort('completed_trainings')}
              >
                <div className="flex items-center gap-1 justify-center">
                  Total
                  <SortIcon columnKey="completed_trainings" sortConfig={sortConfig} />
                </div>
              </TableHead>
              <TableHead className="text-blue-200 text-center">
                Last 30 Days
              </TableHead>
              <TableHead className="text-blue-200">
                Alerts
              </TableHead>
              <TableHead className="text-blue-200 text-right">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAndSortedClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-blue-300">
                  No clients found matching your filters
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedClients.map((client) => (
                <TableRow 
                  key={client.id} 
                  className="border-blue-400/10 hover:bg-blue-900/30 transition-colors"
                  data-testid={`client-row-${client.id}`}
                >
                  <TableCell className="font-medium">
                    <div>
                      <p className="text-white">{client.full_name}</p>
                      <p className="text-blue-400 text-xs">{client.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge client={client} />
                  </TableCell>
                  <TableCell className="text-blue-200 text-sm">
                    {formatDate(client.last_training_date)}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-white font-semibold">
                      {client.completed_trainings || 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <FrequencyBadge frequency={client.training_frequency_30d || 0} />
                  </TableCell>
                  <TableCell>
                    <AlertsIndicator client={client} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/admin/clients/${client.id}`)}
                        className="p-2 text-blue-400 hover:text-white hover:bg-blue-500/20 rounded transition-colors"
                        title="View client details"
                        data-testid={`view-client-${client.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/admin/clients/${client.id}?edit=true`)}
                        className="p-2 text-yellow-400 hover:text-white hover:bg-yellow-500/20 rounded transition-colors"
                        title="Edit client"
                        data-testid={`edit-client-${client.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/admin/clients/${client.id}?tab=planner`)}
                        className="p-2 text-purple-400 hover:text-white hover:bg-purple-500/20 rounded transition-colors"
                        title="Open planner"
                        data-testid={`calendar-client-${client.id}`}
                      >
                        <Calendar className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => printClientIntakeForm(client)}
                        className="p-2 text-green-400 hover:text-white hover:bg-green-500/20 rounded transition-colors"
                        title="Imprimir ficha del cliente (con datos)"
                        data-testid={`print-client-${client.id}`}
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ClientsTable;
